/**
 * Remark plugin: remarkGroupedCards
 *
 * Transforms `<div class="grouped-cards">` HTML wrappers into structured mdast nodes.
 * This plugin handles the LLM-generated format where multiple blockquote cards
 * are wrapped in a container div for visual grouping.
 *
 * Handles three input formats:
 * 1. Inline HTML: `<div class="grouped-cards">\n> Card 1\n> Card 2\n</div>`
 * 2. Split HTML: Opening and closing tags separated by markdown blocks
 * 3. Edge cases: Unbalanced tags, nested divs, mixed content
 *
 * @example
 * Input markdown:
 * ```markdown
 * <div class="grouped-cards">
 * > ✅ **Win:** Closed 3 deals
 * > ⚠️ **Warning:** Pipeline needs attention
 * </div>
 * ```
 *
 * Output mdast:
 * ```typescript
 * {
 *   type: 'grouped_cards',
 *   children: [
 *     { type: 'blockquote', children: [...] },
 *     { type: 'blockquote', children: [...] }
 *   ],
 *   data: {
 *     hName: 'div',
 *     hProperties: { className: ['grouped-cards'] }
 *   }
 * }
 * ```
 */

import type { Blockquote, HTML, Node, Parent, Root } from 'mdast';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { CONTINUE, SKIP, visit } from 'unist-util-visit';
import type { GroupedCardsNode } from './types';
import { detectCardType } from './types';

/**
 * Regex patterns for HTML tag detection
 */
const GROUPED_CARDS_OPEN_REGEX = /<div\s+class=["']grouped-cards["']\s*>/i;
const GROUPED_CARDS_CLOSE_REGEX = /<\/div>/i;

function isHtmlNode(node: unknown): node is HTML {
  if (!node || typeof node !== 'object') {
    return false;
  }
  const maybeNode = node as { type?: unknown; value?: unknown };
  return maybeNode.type === 'html' && typeof maybeNode.value === 'string';
}

/**
 * Check if an HTML node contains inline grouped-cards content
 * (opening tag, content, and closing tag all in one node)
 *
 * Updated to handle both:
 * 1. Proper blockquotes (lines starting with >)
 * 2. Missing blockquotes (lines with card indicators but no >)
 */
function isInlineGroupedCards(node: Node): node is HTML {
  if (node.type !== 'html') {
    return false;
  }
  const htmlValue = (node as HTML).value;

  // Must have both opening and closing tags
  if (!GROUPED_CARDS_OPEN_REGEX.test(htmlValue) || !GROUPED_CARDS_CLOSE_REGEX.test(htmlValue)) {
    return false;
  }

  // Extract content between tags
  const content = htmlValue
    .replace(GROUPED_CARDS_OPEN_REGEX, '')
    .replace(GROUPED_CARDS_CLOSE_REGEX, '');

  // Check if content has either:
  // 1. Blockquote markers (>), OR
  // 2. Card indicators (✅ ⚠️ 💬 📋)
  const hasBlockquoteMarkers = content.includes('>');
  const hasCardIndicators = detectCardType(content) !== null;

  return hasBlockquoteMarkers || hasCardIndicators;
}

/**
 * Parse inline grouped-cards HTML into separate blockquote strings
 * Extracts blockquote lines from HTML content
 *
 * Handles two formats:
 * 1. Proper blockquotes: Lines starting with '>'
 * 2. Missing blockquotes: Lines with card indicators (✅ ⚠️ 💬 📋) but no '>'
 *
 * A new card starts when:
 * - Line starts with '>' (blockquote marker), OR
 * - Line starts with a card emoji/indicator
 *
 * Lines without card indicators that follow a card line are treated as
 * continuation of that card (multi-line cards).
 */
function parseInlineGroupedCards(htmlContent: string): string[] {
  // Remove the div tags
  const content = htmlContent
    .replace(GROUPED_CARDS_OPEN_REGEX, '')
    .replace(GROUPED_CARDS_CLOSE_REGEX, '');

  // Split by lines and collect blockquote content
  const lines = content.split('\n');
  const blockquotes: string[] = [];
  let currentBlockquote: string[] = [];
  let inBlockquote = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip empty lines between cards
    if (trimmed === '') {
      // Empty line ends current blockquote
      if (inBlockquote && currentBlockquote.length > 0) {
        blockquotes.push(currentBlockquote.join('\n'));
        currentBlockquote = [];
        inBlockquote = false;
      }
      continue;
    }

    // Check if line starts with > (proper blockquote format)
    const startsWithBlockquote = trimmed.startsWith('>');

    // Check if line looks like the START of a card (has card indicators at the beginning)
    const startsCard = detectCardType(trimmed) !== null;

    // This line starts a new card
    if (startsWithBlockquote || startsCard) {
      // If we have a previous blockquote, save it and start a new one
      if (inBlockquote && currentBlockquote.length > 0) {
        blockquotes.push(currentBlockquote.join('\n'));
        currentBlockquote = [];
      }

      // Add the line (with or without the > prefix)
      currentBlockquote.push(trimmed);
      inBlockquote = true;
    }
    // Continuation line (part of current card)
    else if (inBlockquote) {
      // This is a continuation of the current card
      currentBlockquote.push(trimmed);
    }
    // Non-card content when not in a card - ignore
  }

  // Don't forget the last blockquote
  if (currentBlockquote.length > 0) {
    blockquotes.push(currentBlockquote.join('\n'));
  }

  return blockquotes;
}

/**
 * Create blockquote mdast nodes from markdown text
 *
 * This function properly parses the markdown content inside blockquotes,
 * preserving formatting like bold, italic, links, code, etc.
 *
 * Handles both:
 * 1. Proper blockquote format: Lines starting with '>'
 * 2. Missing blockquote markers: Plain text that should be treated as blockquote content
 */
function createBlockquoteFromMarkdown(markdownText: string): Blockquote | null {
  // Remove leading '>' and whitespace from each line (if present)
  const lines = markdownText.split('\n').map((line) => {
    const match = line.match(/^>\s*(.*)/);
    return match ? match[1] : line;
  });

  const content = lines.join('\n').trim();
  if (!content) {
    return null;
  }

  // Parse the markdown content to get properly structured nodes
  const processor = unified().use(remarkParse);
  const parsedTree = processor.parse(content);

  // Extract the children from the parsed root
  // The parsed content will have paragraph nodes, which we use as blockquote children
  const children = (parsedTree as Root).children;

  if (children.length === 0) {
    return null;
  }

  return {
    type: 'blockquote',
    children: children as any, // Type cast needed because blockquote children can be various block content
  };
}

/**
 * Check if an HTML node is a grouped-cards opening tag
 */
/**
 * Check if node contains a grouped-cards opening tag
 * Handles both direct HTML nodes and HTML inside paragraph nodes
 */
function isGroupedCardsOpenTag(node: Node): boolean {
  // Direct HTML node with opening tag
  if (node.type === 'html' && GROUPED_CARDS_OPEN_REGEX.test((node as HTML).value)) {
    return true;
  }

  // Paragraph node containing HTML with opening tag
  if (node.type === 'paragraph' && 'children' in node) {
    const para = node as Parent;
    return para.children.some(
      (child) => child.type === 'html' && GROUPED_CARDS_OPEN_REGEX.test((child as HTML).value)
    );
  }

  return false;
}

/**
 * Check if node is a blockquote (potential card)
 */
function isBlockquote(node: Node): node is Blockquote {
  return node.type === 'blockquote';
}

/**
 * Create a grouped_cards mdast node
 */
function createGroupedCardsNode(children: Blockquote[]): GroupedCardsNode {
  return {
    type: 'grouped_cards',
    children,
    data: {
      hName: 'div',
      hProperties: {
        className: ['grouped-cards'],
      },
    },
  };
}

/**
 * Extract text content from a node (helper function for card detection)
 */
function extractTextContent(node: Node): string {
  if ('value' in node && typeof node.value === 'string') {
    return node.value;
  }
  if ('children' in node && Array.isArray((node as Parent).children)) {
    return (node as Parent).children.map((child) => extractTextContent(child)).join(' ');
  }
  return '';
}

/**
 * Try to extract inline HTML content from split format nodes
 * This handles cases where remark-parse creates paragraph nodes containing HTML
 * that should have been parsed as inline grouped-cards
 */
function extractInlineContentFromSplitFormat(
  parent: Parent,
  startIndex: number,
  endIndex: number
): string | null {
  // Look for a single paragraph node between the tags that might contain HTML
  if (endIndex - startIndex === 2) {
    const middleNode = parent.children[startIndex + 1];
    if (middleNode.type === 'paragraph' && 'children' in middleNode) {
      const para = middleNode as Parent;
      // Check if this paragraph contains HTML children with grouped-cards content
      for (const child of para.children) {
        if (child.type === 'html') {
          const htmlValue = (child as HTML).value;
          if (
            GROUPED_CARDS_OPEN_REGEX.test(htmlValue) &&
            GROUPED_CARDS_CLOSE_REGEX.test(htmlValue)
          ) {
            return htmlValue;
          }
        }
      }
    }
  }

  // Try to reconstruct inline content from multiple nodes
  const parts: string[] = [];
  let foundOpening = false;

  for (let i = startIndex; i <= endIndex; i++) {
    const node = parent.children[i];

    if (node.type === 'html') {
      const htmlValue = (node as HTML).value;
      if (GROUPED_CARDS_OPEN_REGEX.test(htmlValue)) {
        parts.push(htmlValue);
        foundOpening = true;
      } else if (GROUPED_CARDS_CLOSE_REGEX.test(htmlValue)) {
        parts.push(htmlValue);
        break;
      }
    } else if (foundOpening && node.type === 'paragraph') {
      const textContent = extractTextContent(node);
      if (textContent.trim()) {
        parts.push(textContent);
      }
    }
  }

  if (parts.length > 0) {
    return parts.join('\n');
  }

  return null;
}

/**
 * Extract or create blockquotes from content between grouped-cards tags
 *
 * Handles three scenarios:
 * 1. Proper blockquotes (from compliant LLM output) - used as-is
 * 2. Plain paragraphs with card indicators (from non-compliant LLM output) - wrapped into blockquotes
 * 3. Mixed content (combination of the above)
 *
 * This function makes the renderer tolerant of LLM format variance, fixing the issue
 * where LLMs sometimes omit the `>` blockquote markers inside grouped-cards divs.
 */
function extractBlockquotes(parent: Parent, startIndex: number, endIndex: number): Blockquote[] {
  const blockquotes: Blockquote[] = [];

  for (let i = startIndex + 1; i < endIndex; i++) {
    const node = parent.children[i];

    // Case 1: Already a blockquote (proper format)
    if (isBlockquote(node)) {
      blockquotes.push(node);
      continue;
    }

    // Case 2: Plain paragraph with card indicators (missing > marker)
    // This handles the bug where LLMs generate cards without blockquote syntax
    if (node.type === 'paragraph' && 'children' in node) {
      const textContent = extractTextContent(node);

      // Skip empty paragraphs (blank lines)
      if (!textContent.trim()) {
        continue;
      }

      // Check if it looks like a card (has emoji indicators like ✅ ⚠️ 💬 📋)
      if (detectCardType(textContent)) {
        // Wrap the paragraph in a blockquote so it gets processed as a card
        const syntheticBlockquote: Blockquote = {
          type: 'blockquote',
          children: [node as any],
        };
        blockquotes.push(syntheticBlockquote);
      }
    }

    // Case 3: Other content (ignore - might be empty lines, HTML, etc.)
  }

  return blockquotes;
}

/**
 * Find a best-effort end index for malformed grouped-cards blocks where
 * the closing </div> tag is missing. We consume only contiguous card-like
 * content so unrelated markdown is not accidentally swallowed.
 */
function findFallbackEndIndex(parent: Parent, startIndex: number): number {
  let endIndex = startIndex;

  for (let i = startIndex + 1; i < parent.children.length; i++) {
    const node = parent.children[i];

    if (isBlockquote(node)) {
      endIndex = i;
      continue;
    }

    if (node.type === 'paragraph' && 'children' in node) {
      const textContent = extractTextContent(node).trim();
      if (!textContent || detectCardType(textContent)) {
        endIndex = i;
        continue;
      }
    }

    break;
  }

  return endIndex;
}

/**
 * Find matching closing tag for an opening tag
 *
 * @param parent - Parent node containing children to search
 * @param startIndex - Index of opening tag
 * @returns Index of matching closing tag, or -1 if not found
 */
/**
 * Find the index of the closing </div> tag for grouped-cards
 *
 * Handles:
 * - HTML nodes with closing tag
 * - Paragraph nodes containing HTML children with closing tag
 * - Nested divs (tracks depth)
 */
function findClosingTag(parent: Parent, startIndex: number): number {
  let depth = 1; // Track nesting depth for nested divs

  for (let i = startIndex + 1; i < parent.children.length; i++) {
    const node = parent.children[i];

    // Check direct HTML nodes
    if (node.type === 'html') {
      const htmlValue = (node as HTML).value;

      // Check for nested opening tags
      if (GROUPED_CARDS_OPEN_REGEX.test(htmlValue)) {
        depth++;
      }
      // Check for closing tags
      else if (GROUPED_CARDS_CLOSE_REGEX.test(htmlValue)) {
        depth--;
        if (depth === 0) {
          return i;
        }
      }
    }
    // Check paragraph nodes that might contain HTML children
    else if (node.type === 'paragraph' && 'children' in node) {
      const para = node as Parent;
      for (const child of para.children) {
        if (child.type === 'html') {
          const htmlValue = (child as HTML).value;

          // Check for nested opening tags
          if (GROUPED_CARDS_OPEN_REGEX.test(htmlValue)) {
            depth++;
          }
          // Check for closing tags
          else if (GROUPED_CARDS_CLOSE_REGEX.test(htmlValue)) {
            depth--;
            if (depth === 0) {
              return i;
            }
          }
        }
      }
    }
  }

  return -1; // No matching closing tag found
}

/**
 * Remark plugin to transform grouped-cards HTML into mdast nodes
 */
export default function remarkGroupedCards() {
  return (tree: Root) => {
    visit(tree, (node, index, parent) => {
      // Only process nodes with parents
      if (!parent || index === undefined) {
        return CONTINUE;
      }

      // Check for inline grouped-cards HTML (all in one node)
      if (isInlineGroupedCards(node)) {
        const htmlValue = (node as HTML).value;
        const blockquoteTexts = parseInlineGroupedCards(htmlValue);

        // Convert blockquote texts to blockquote nodes
        const blockquotes = blockquoteTexts
          .map(createBlockquoteFromMarkdown)
          .filter((bq): bq is Blockquote => bq !== null);

        if (blockquotes.length === 0) {
          console.warn(
            'remarkGroupedCards: Found inline grouped-cards HTML but no valid blockquotes. Skipping transformation.'
          );
          return CONTINUE;
        }

        // Create grouped_cards node
        const groupedCardsNode = createGroupedCardsNode(blockquotes);

        // Replace the HTML node with the grouped node
        parent.children[index] = groupedCardsNode as any;

        // Skip the newly created node
        return [SKIP, index];
      }

      // Recover malformed inline grouped-cards where the LLM omitted the
      // closing </div> but still emitted card content in this HTML node.
      const currentChild = parent.children[index];
      if (isHtmlNode(currentChild)) {
        const htmlValue = currentChild.value;
        const missingCloseInParent =
          GROUPED_CARDS_OPEN_REGEX.test(htmlValue) && findClosingTag(parent, index) === -1;
        if (missingCloseInParent) {
          const blockquotes = parseInlineGroupedCards(htmlValue)
            .map(createBlockquoteFromMarkdown)
            .filter((bq): bq is Blockquote => bq !== null);

          if (blockquotes.length > 0) {
            const groupedCardsNode = createGroupedCardsNode(blockquotes);
            parent.children[index] = groupedCardsNode as any;
            return [SKIP, index];
          }
        }
      }

      // Look for grouped-cards opening tags (split format)
      if (!isGroupedCardsOpenTag(node)) {
        return CONTINUE;
      }

      // Find matching closing tag
      const closeIndex = findClosingTag(parent, index);

      // Edge case: Unbalanced tags (no closing tag found)
      if (closeIndex === -1) {
        const fallbackEndIndex = findFallbackEndIndex(parent, index);
        if (fallbackEndIndex > index) {
          const blockquotes = extractBlockquotes(parent, index, fallbackEndIndex + 1);
          if (blockquotes.length > 0) {
            const groupedCardsNode = createGroupedCardsNode(blockquotes);
            const removeCount = fallbackEndIndex - index + 1;
            parent.children.splice(index, removeCount, groupedCardsNode as any);
            return [SKIP, index];
          }
        }

        // Malformed wrapper with no recoverable card content.
        return CONTINUE;
      }

      // Extract blockquotes between tags
      let blockquotes = extractBlockquotes(parent, index, closeIndex);

      // If we found fewer blockquotes than expected, try to extract inline content
      // This handles cases where remark-parse creates paragraph nodes that contain
      // multiple cards' text or HTML content that should be parsed as inline
      if (blockquotes.length === 0 || blockquotes.length === 1) {
        const potentialInlineContent = extractInlineContentFromSplitFormat(
          parent,
          index,
          closeIndex
        );
        if (potentialInlineContent) {
          const inlineBlockquotes = parseInlineGroupedCards(potentialInlineContent)
            .map(createBlockquoteFromMarkdown)
            .filter((bq): bq is Blockquote => bq !== null);

          // Use inline parsing results if we found more cards
          if (inlineBlockquotes.length > blockquotes.length) {
            blockquotes = inlineBlockquotes;
          }
        }
      }

      // Edge case: No blockquotes found (empty or invalid content)
      if (blockquotes.length === 0) {
        console.warn(
          'remarkGroupedCards: Found grouped-cards wrapper with no card content. ' +
            'Content may be missing blockquote markers (>) or card indicators (✅ ⚠️ 💬 📋). ' +
            'Skipping transformation.'
        );
        return CONTINUE;
      }

      // Create grouped_cards node
      const groupedCardsNode = createGroupedCardsNode(blockquotes);

      // Replace the opening tag, blockquotes, and closing tag with the grouped node
      // Remove closeIndex - index + 1 elements starting at index
      const removeCount = closeIndex - index + 1;
      parent.children.splice(index, removeCount, groupedCardsNode as any);

      // Skip the newly created node to avoid re-processing
      return [SKIP, index];
    });
  };
}
